import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowRight, Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import ReferralHero from './ReferralHero';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* Props: { refCode, onSuccess }
 *
 * ReferralForm is the stateful core of the referral page. It:
 *   - Fetches referrer info for the hero (referrerName is local here)
 *   - Tracks landing-viewed and form-started events
 *   - Owns form, errors, loading, formStarted, referrerName state
 *   - Renders ReferralHero internally (needs referrerName)
 *   - Calls onSuccess() when submission succeeds (parent handles success screen)
 */
const ReferralForm = ({ refCode, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formStarted, setFormStarted] = useState(false);
  const [referrerName, setReferrerName] = useState('');
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    company_name: '',
    role: 'business',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!refCode) return;

    fetch(`${BACKEND_URL}/api/referrals/referrer-info?ref_code=${refCode}`)
      .then(res => res.json())
      .then(data => {
        if (data.referrer_name) setReferrerName(data.referrer_name);
      })
      .catch(() => {
        setReferrerName('');
      });

    fetch(`${BACKEND_URL}/api/referrals/track/landing-viewed`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ref_code: refCode }),
    }).catch(() => {});
  }, [refCode]);

  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = t('referral.landing.errRequired');
    if (!form.last_name.trim()) errs.last_name = t('referral.landing.errRequired');
    if (!form.email.trim() || !form.email.includes('@'))
      errs.email = t('referral.landing.errEmail');
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/referrals/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, ref_code: refCode }),
      });
      const json = await res.json();
      if (!res.ok) {
        toast.error(json.detail || t('referral.landing.errSubmit'));
        return;
      }
      onSuccess();
    } catch {
      toast.error(t('referral.landing.errSubmit'));
    } finally {
      setLoading(false);
    }
  };

  const set = (field) => (e) => {
    if (!formStarted) {
      setFormStarted(true);
      fetch(`${BACKEND_URL}/api/referrals/track/form-started`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ref_code: refCode }),
      }).catch(() => {});
    }
    setForm((prev) => ({ ...prev, [field]: e.target?.value ?? e }));
  };

  return (
    <>
      <ReferralHero referrerName={referrerName} />

      <section aria-label="Registration Form">
        <Card>
          <CardHeader>
            <CardTitle as="h2">{t('referral.landing.formTitle')}</CardTitle>
            <CardDescription>{t('referral.landing.formSubtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="first_name">{t('referral.landing.firstName')}</Label>
                  <Input
                    id="first_name"
                    value={form.first_name}
                    onChange={set('first_name')}
                    placeholder={t('referral.landing.firstNamePlaceholder')}
                  />
                  {errors.first_name && (
                    <p className="text-destructive text-xs">{errors.first_name}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="last_name">{t('referral.landing.lastName')}</Label>
                  <Input
                    id="last_name"
                    value={form.last_name}
                    onChange={set('last_name')}
                    placeholder={t('referral.landing.lastNamePlaceholder')}
                  />
                  {errors.last_name && (
                    <p className="text-destructive text-xs">{errors.last_name}</p>
                  )}
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="email">{t('referral.landing.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  placeholder={t('referral.landing.emailPlaceholder')}
                />
                {errors.email && (
                  <p className="text-destructive text-xs">{errors.email}</p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="company_name">
                  {t('referral.landing.company')}{' '}
                  <span className="text-muted-foreground text-xs">({t('referral.landing.optional')})</span>
                </Label>
                <Input
                  id="company_name"
                  value={form.company_name}
                  onChange={set('company_name')}
                  placeholder={t('referral.landing.companyPlaceholder')}
                />
              </div>

              <div className="space-y-1">
                <Label>{t('referral.landing.role')}</Label>
                <Select value={form.role} onValueChange={set('role')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="business">{t('referral.landing.roleBusiness')}</SelectItem>
                    <SelectItem value="vendor">{t('referral.landing.roleVendor')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('referral.landing.submit')}
              </Button>
              <p className="text-center text-xs text-muted-foreground mt-2">
                <Lock className="inline h-3 w-3 mr-1" />
                {t('referral.landing.noCreditCard')}
              </p>
            </form>
          </CardContent>
        </Card>
      </section>
    </>
  );
};

export default ReferralForm;
